interface StorageBarProps {
  usedGB: number;
  totalGB: number;
  label?: string;
  compact?: boolean;
}

function formatGB(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)} TB`;
  if (n >= 1) return `${n.toFixed(1)} GB`;
  return `${(n * 1024).toFixed(0)} MB`;
}

function storageColor(ratio: number): string {
  if (ratio >= 0.95) return '#ef4444';
  if (ratio >= 0.75) return '#f59e0b';
  if (ratio >= 0.50) return '#d97706';
  return '#22c55e';
}

function storageBgColor(ratio: number): string {
  if (ratio >= 0.95) return 'bg-red-50';
  if (ratio >= 0.75) return 'bg-amber-50';
  if (ratio >= 0.50) return 'bg-orange-50';
  return 'bg-emerald-50';
}

function storageTextColor(ratio: number): string {
  if (ratio >= 0.95) return 'text-red-700';
  if (ratio >= 0.75) return 'text-amber-700';
  if (ratio >= 0.50) return 'text-orange-700';
  return 'text-emerald-700';
}

export default function StorageBar({ usedGB, totalGB, label, compact }: StorageBarProps) {
  const ratio = totalGB > 0 ? usedGB / totalGB : 0;
  const pct = Math.min(100, Math.round(ratio * 100));

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden min-w-[60px]">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: storageColor(ratio) }}
          ></div>
        </div>
        <span className={`text-[10px] font-mono font-semibold whitespace-nowrap ${storageTextColor(ratio)}`}>
          {pct}%
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-medium">{label}</span>
          <span className={`text-[10px] font-semibold ${storageTextColor(ratio)}`}>{pct}% usado</span>
        </div>
      )}
      <div className="w-full h-3 bg-background-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all relative"
          style={{ width: `${pct}%`, backgroundColor: storageColor(ratio) }}
        >
          {pct > 15 && (
            <span className="absolute inset-0 flex items-center justify-center text-background-50 text-[9px] font-bold">
              {formatGB(usedGB)}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-foreground-300 text-[9px]">{formatGB(usedGB)} usado</span>
        <span className="text-foreground-300 text-[9px]">{formatGB(totalGB)} total</span>
      </div>
    </div>
  );
}